import { useReadOnly } from 'utils/lifecycle'
import React, { useState } from 'react'
import {
  Button,
  Box,
  CardActions,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material'
import HomeIcon from "@mui/icons-material/HomeOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import FavoriteIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteFilledIcon from "@mui/icons-material/Favorite";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import ClaimPopup from "../ClaimPopup";
import InfoPopup from "../InfoPopup";
// import { useStyles } from './styles'
import { ButtonContainer, StyledButton } from './styles';
import useFavoriteOffer from '../useFavoriteOffer'
import api from 'utils/api'

const formatPhone = (input = "") => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  let formatted = "";
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};

const isPhoneFormatted = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

const formatPhoneIfNeeded = (phone) => {
  if (!phone) return phone;
  // If already formatted, return as-is
  if (isPhoneFormatted(phone)) return phone;
  // Otherwise, format it
  return formatPhone(phone);
};

const OfferActions = ({
  active,
  businessImageUrl,
  businessName,
  claimsRemaining,
  description,
  termsAndConditions,
  distance,
  endDate,
  headline1,
  headline2,
  id,
  imageUrl,
  isPreview,
  phone,
  showPhone,
  address,
  showAddress,
  offerLocation,
  revalidateList,
  beforeClaimAction,
  compact = false,
  allowUnavailableActions = false,
}) => {
  const readOnly = useReadOnly()
  // const classes = useStyles()
  const { isFavorite, isUpdating, toggleFavorite } = useFavoriteOffer(id, {
    disabled: isPreview || readOnly,
    revalidateList,
  })
  const [showClaimPopup, setShowClaimPopup] = useState(false)
  const [showInfoPopup, setShowInfoPopup] = useState(false)
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [showLocationPopup, setShowLocationPopup] = useState(false)
  const [showMapsChoice, setShowMapsChoice] = useState(false)
  const secondaryActionDisabled = isPreview || (!active && !allowUnavailableActions)
  const recordInteraction = action => {
    if (!isPreview) Promise.resolve(api.post(`/common/offers/${id}/interactions`, { action })).catch(() => {})
  }

  // Extract address from offerLocation if address prop is not available
  // Use the first address that has showAddress === true, or fallback to first address
  const getResolvedAddress = () => {
    if (address) return address;
    if (offerLocation?.local && offerLocation.local.length > 0) {
      // Find first address with showAddress === true
      const addressWithToggle = offerLocation.local.find(addr => addr.showAddress === true);
      if (addressWithToggle) return addressWithToggle.address;
      // Fallback to first address if none have toggle on
      return offerLocation.local[0].address;
    }
    return null;
  };
  
  const resolvedAddress = getResolvedAddress();
  
  // Show location icon only if showAddress is true (exact same logic as showPhone)
  const resolvedShowAddress = showAddress === true;
  
  const handleOpenGoogleMaps = () => {
    if (!resolvedAddress) return;
    recordInteraction('address');
    const encodedAddress = encodeURIComponent(resolvedAddress);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    setShowMapsChoice(false);
    setShowLocationPopup(false);
  };

  const handleOpenAppleMaps = () => {
    if (!resolvedAddress) return;
    recordInteraction('address');
    const encodedAddress = encodeURIComponent(resolvedAddress);
    window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    setShowMapsChoice(false);
    setShowLocationPopup(false);
  };

  const handleDirectionsClick = () => {
    if (!resolvedAddress) return;
    
    // Detect iOS devices (where both Apple Maps and Google Maps are typically available)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      // On iOS, both apps are typically available, so show choice dialog
      setShowMapsChoice(true);
    } else {
      // On Android/Desktop, Google Maps is the default (Apple Maps not available)
      handleOpenGoogleMaps();
    }
  };

  return (
    <>
      <CardActions 
        disableSpacing
        sx={{ 
          gap: 0,
          flexWrap: beforeClaimAction && !compact ? 'wrap' : 'nowrap',
          ...(compact && beforeClaimAction && {
            px: 0.5,
            '& .MuiIconButton-root': { padding: '3px' },
          }),
          '& > *': { 
            margin: 0, 
            marginLeft: 0, 
            marginRight: 0,
            '&:not(:first-of-type)': {
              marginLeft: '-4px'
            }
          } 
        }}
      >
        {showPhone === true && (
          <Tooltip title="Call business" enterDelay={500} leaveDelay={50}>
            <span style={{ margin: 0, display: 'inline-flex' }}>
              <IconButton
                aria-label="call business"
                disabled={secondaryActionDisabled}
                onClick={() => setShowPhonePopup(true)}
                size="large"
                sx={{ padding: '6px' }}
              >
                <PhoneIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {resolvedShowAddress === true && (
          <Tooltip title="Directions" enterDelay={500} leaveDelay={50}>
            <span style={{ margin: 0, display: 'inline-flex' }}>
              <IconButton
                aria-label="Directions"
                disabled={secondaryActionDisabled}
                onClick={() => setShowLocationPopup(true)}
                size="large"
                sx={{ padding: '6px' }}
              >
                <LocationOnIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Visit website" enterDelay={500} leaveDelay={50}>
          <span style={{ margin: 0, display: 'inline-flex' }}>
            <IconButton
              aria-label="go to website"
              disabled={readOnly || secondaryActionDisabled}
              href={`/api/common/offer_homepage/${id}`}
              target="_blank"
              rel="nofollow"
              size="large"
              sx={{ padding: '6px' }}
            >
              <HomeIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          enterDelay={500}
          leaveDelay={50}
        >
          <span style={{ margin: 0, display: 'inline-flex' }}>
            <IconButton
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              onClick={toggleFavorite}
              disabled={readOnly || isPreview || isUpdating}
              size="large"
              sx={{ padding: '6px' }}
            >
              {isFavorite ? (
                <FavoriteFilledIcon color="error" />
              ) : (
                <FavoriteIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Terms & conditions" enterDelay={500} leaveDelay={50}>
          <span style={{ margin: 0, display: 'inline-flex' }}>
            <IconButton
              aria-label="More information"
              disabled={secondaryActionDisabled}
              onClick={() => { recordInteraction('detail_view'); setShowInfoPopup(true) }}
              size="large"
              sx={{ padding: '6px' }}
            >
              <InfoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <ButtonContainer sx={beforeClaimAction ? { gap: { xs: 0.5, sm: 1 }, flex: '1 0 auto', whiteSpace: 'nowrap' } : undefined}>
          {beforeClaimAction}
          <Tooltip title="Claim offer" enterDelay={500} leaveDelay={50}>
            <span>
              <StyledButton
                color="primary"
                disableElevation
                variant="contained"
                startIcon={<LocalOfferIcon />}
                disabled={readOnly || isPreview || !active}
                onClick={() => setShowClaimPopup(true)}
                sx={{
                  minWidth: 72,
                  fontSize: '0.8rem',
                  py: 0.5,
                  px: 1.5,
                  '& .MuiButton-startIcon': { mr: 0.5 }
                }}
              >
                Claim
              </StyledButton>
            </span>
          </Tooltip>
        </ButtonContainer>
      </CardActions>
  
      {showClaimPopup && (
        <ClaimPopup
          closePopup={() => setShowClaimPopup(false)}
          headline1={headline1}
          headline2={headline2}
          description={description}
          distance={distance}
          claims={claimsRemaining}
          endDate={endDate}
          id={id}
          active={active}
          address={address}
          showAddress={showAddress}
          phone={phone}
          showPhone={showPhone}
          offerLocation={offerLocation}
          revalidateList={revalidateList}
          businessName={businessName}
          businessImageUrl={businessImageUrl}
        />
      )}
  
      {showInfoPopup && (
        <InfoPopup
          active={active}
          address={address}
          businessImageUrl={businessImageUrl}
          businessName={businessName}
          claimsRemaining={claimsRemaining}
          closePopup={() => setShowInfoPopup(false)}
          description={description}
          termsAndConditions={termsAndConditions}
          distance={distance}
          endDate={endDate}
          headline1={headline1}
          headline2={headline2}
          id={id}
          offerLocation={offerLocation}
          openClaim={() => {
            setShowInfoPopup(false);
            setShowClaimPopup(true);
          }}
          phone={phone}
          revalidateList={revalidateList}
          showAddress={showAddress}
          showPhone={showPhone}
          allowUnavailableActions={allowUnavailableActions}
        />
      )}

      <Dialog open={showPhonePopup} onClose={() => setShowPhonePopup(false)}>
        <DialogTitle>{businessName ? `${businessName} ` : 'Business phone'}</DialogTitle>
        <DialogContent>
          {phone ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {formatPhoneIfNeeded(phone)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhoneIcon />}
                  href={`tel:${phone.replace(/\D/g, '')}`}
                  onClick={() => recordInteraction('phone')}
                >
                  Call
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Phone number not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPhonePopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLocationPopup} onClose={() => setShowLocationPopup(false)}>
        <DialogTitle>{businessName ? `${businessName} ` : 'Business address'}</DialogTitle>
        <DialogContent>
          {resolvedAddress ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {resolvedAddress}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LocationOnIcon />}
                  onClick={handleDirectionsClick}
                >
                  Directions
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Address not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationPopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showMapsChoice} onClose={() => setShowMapsChoice(false)}>
        <DialogTitle>Choose maps app</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
            Select your preferred maps application
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocationOnIcon />}
              onClick={handleOpenGoogleMaps}
              sx={{ minWidth: 140 }}
            >
              Google Maps
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocationOnIcon />}
              onClick={handleOpenAppleMaps}
              sx={{ minWidth: 140 }}
            >
              Apple Maps
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapsChoice(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
  
}

export default OfferActions
